import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClickedOutsideDirective } from '../../../../../shared/directives/clicked-outside.directive';
import { environment } from '../../../../../environments/environment';

export interface RuleCondition {
  field: 'segments';
  value: string[];
  operator: 'ANY' | 'ALL';
}

export interface RuleGroup {
  operator: 'AND' | 'OR';
  conditions: RuleCondition[];
}

export interface RuleLogic {
  operator: 'AND' | 'OR';
  groups: RuleGroup[];
}

// Kept for backward-compat with create-segment.ts
export interface GroupPair {
  groupA: { segments: string[] };
  groupB: { segments: string[] };
}

interface ConditionState {
  segments: string[];
  segmentOperator: 'ANY' | 'ALL';
  dropdownOpen: boolean;
  searchTerm: string;
}

interface GroupState {
  conditions: ConditionState[];
  conditionOperator: 'AND' | 'OR';
}

@Component({
  selector: 'app-rule-based-mapping',
  standalone: true,
  imports: [CommonModule, FormsModule, ClickedOutsideDirective],
  templateUrl: './rule-based-mapping.html',
  styleUrl: './rule-based-mapping.scss',
})
export class RuleBasedMappingComponent implements OnInit, OnChanges {
  @Input() availableSegments: { name: string; value: string }[] = [];
  @Input() isViewOnly: boolean = false;
  @Input() initialRuleLogic: RuleLogic | null = null;
  @Input() submitted: boolean = false;

  @Output() ruleLogicChange = new EventEmitter<RuleLogic>();

  groups: GroupState[] = [];
  groupOperator: 'AND' | 'OR' = 'AND';
  isHelpExpanded = false;
  isJsonExpanded = false;
  readonly enableRuleLogicJson = environment.enableRuleLogicJson;

  readonly MAX_GROUPS = 2;
  readonly MAX_CONDITIONS = 2;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.groups.length === 0) {
      if (this.initialRuleLogic) {
        this.populateFromRuleLogic(this.initialRuleLogic);
      } else {
        this.groups = [this.makeEmptyGroup()];
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialRuleLogic']) {
      if (this.initialRuleLogic) {
        this.populateFromRuleLogic(this.initialRuleLogic);
      } else if (this.groups.length === 0) {
        this.groups = [this.makeEmptyGroup()];
      }
    }
  }

  private makeEmptyCondition(): ConditionState {
    return { segments: [], segmentOperator: 'ALL', dropdownOpen: false, searchTerm: '' };
  }

  private makeEmptyGroup(): GroupState {
    return { conditions: [this.makeEmptyCondition()], conditionOperator: 'AND' };
  }

  // ─── Groups ───────────────────────────────────────────────────────────────

  addGroup(): void {
    if (this.groups.length >= this.MAX_GROUPS) return;
    this.groups = [...this.groups, this.makeEmptyGroup()];
    this.emit();
    this.cdr.detectChanges();
  }

  removeGroup(gIdx: number): void {
    this.groups = this.groups.filter((_, i) => i !== gIdx);
    if (this.groups.length === 0) this.groups = [this.makeEmptyGroup()];
    this.emit();
    this.cdr.detectChanges();
  }

  setGroupOperator(op: 'AND' | 'OR'): void {
    this.groupOperator = op;
    this.emit();
  }

  // ─── Conditions ───────────────────────────────────────────────────────────

  addCondition(gIdx: number): void {
    const g = this.groups[gIdx];
    if (!g || g.conditions.length >= this.MAX_CONDITIONS) return;
    this.groups = this.groups.map((group, i) =>
      i === gIdx ? { ...group, conditions: [...group.conditions, this.makeEmptyCondition()] } : group
    );
    this.emit();
    this.cdr.detectChanges();
  }

  removeCondition(gIdx: number, cIdx: number): void {
    this.groups = this.groups.map((group, i) => {
      if (i !== gIdx) return group;
      const updated = group.conditions.filter((_, j) => j !== cIdx);
      return { ...group, conditions: updated.length > 0 ? updated : [this.makeEmptyCondition()] };
    });
    this.emit();
    this.cdr.detectChanges();
  }

  setConditionOperator(gIdx: number, op: 'AND' | 'OR'): void {
    this.groups = this.groups.map((group, i) =>
      i === gIdx ? { ...group, conditionOperator: op } : group
    );
    this.emit();
  }

  // ─── Segment operator ─────────────────────────────────────────────────────

  private normalizeCondition(cond: ConditionState): ConditionState {
    return cond.segments.length <= 1 ? { ...cond, segmentOperator: 'ALL' } : cond;
  }

  setSegmentOperator(gIdx: number, cIdx: number, op: 'ANY' | 'ALL'): void {
    if (this.isViewOnly) return;
    this.groups = this.groups.map((group, gi) =>
      gi === gIdx
        ? {
            ...group,
            conditions: group.conditions.map((cond, ci) =>
              ci === cIdx ? { ...cond, segmentOperator: op } : cond
            ),
          }
        : group
    );
    this.emit();
  }

  // ─── Dropdown ─────────────────────────────────────────────────────────────

  toggleDropdown(gIdx: number, cIdx: number, event: Event): void {
    event.stopPropagation();
    const isOpen = this.groups[gIdx].conditions[cIdx].dropdownOpen;
    this.groups = this.groups.map(g => ({
      ...g,
      conditions: g.conditions.map(c => ({ ...c, dropdownOpen: false })),
    }));
    if (!isOpen) {
      this.groups[gIdx].conditions[cIdx].dropdownOpen = true;
    }
    this.cdr.detectChanges();
  }

  closeDropdown(gIdx: number, cIdx: number): void {
    if (this.groups[gIdx]?.conditions[cIdx]) {
      this.groups[gIdx].conditions[cIdx].dropdownOpen = false;
      this.cdr.detectChanges();
    }
  }

  // ─── Segment selection ────────────────────────────────────────────────────

  toggleSegment(gIdx: number, cIdx: number, segValue: string, event: Event): void {
    event.stopPropagation();
    const condition = this.groups[gIdx]?.conditions[cIdx];
    if (!condition) return;
    const isSelected = condition.segments.includes(segValue);
    this.groups = this.groups.map((group, gi) =>
      gi === gIdx
        ? {
            ...group,
            conditions: group.conditions.map((cond, ci) =>
              ci === cIdx
                ? this.normalizeCondition({
                    ...cond,
                    segments: isSelected
                      ? cond.segments.filter(s => s !== segValue)
                      : [...cond.segments, segValue],
                  })
                : cond
            ),
          }
        : group
    );
    this.emit();
    this.cdr.detectChanges();
  }

  removeSegment(gIdx: number, cIdx: number, segIdx: number, event: Event): void {
    event.stopPropagation();
    this.groups = this.groups.map((group, gi) =>
      gi === gIdx
        ? {
            ...group,
            conditions: group.conditions.map((cond, ci) =>
              ci === cIdx
                ? this.normalizeCondition({ ...cond, segments: cond.segments.filter((_, j) => j !== segIdx) })
                : cond
            ),
          }
        : group
    );
    this.emit();
    this.cdr.detectChanges();
  }

  isSegmentSelected(gIdx: number, cIdx: number, segValue: string): boolean {
    return this.groups[gIdx]?.conditions[cIdx]?.segments.includes(segValue) ?? false;
  }

  isAllSelected(gIdx: number, cIdx: number): boolean {
    const segs = this.groups[gIdx]?.conditions[cIdx]?.segments ?? [];
    const all = this.availableSegments;
    return all.length > 0 && all.every(s => segs.includes(s.value));
  }

  toggleSelectAll(gIdx: number, cIdx: number, event: Event): void {
    event.stopPropagation();
    const isAll = this.isAllSelected(gIdx, cIdx);
    this.groups = this.groups.map((group, gi) =>
      gi === gIdx
        ? {
            ...group,
            conditions: group.conditions.map((cond, ci) =>
              ci === cIdx
                ? this.normalizeCondition({ ...cond, segments: isAll ? [] : this.availableSegments.map(s => s.value) })
                : cond
            ),
          }
        : group
    );
    this.emit();
    this.cdr.detectChanges();
  }

  clearAllSegments(gIdx: number, cIdx: number, event: Event): void {
    event.stopPropagation();
    this.groups = this.groups.map((group, gi) =>
      gi === gIdx
        ? {
            ...group,
            conditions: group.conditions.map((cond, ci) =>
              ci === cIdx ? { ...cond, segments: [], segmentOperator: 'ALL' } : cond
            ),
          }
        : group
    );
    this.emit();
    this.cdr.detectChanges();
  }

  // ─── Filtering / Display ──────────────────────────────────────────────────

  getFilteredSegments(gIdx: number, cIdx: number): { name: string; value: string }[] {
    const condition = this.groups[gIdx]?.conditions[cIdx];
    if (!condition) return [];
    const search = condition.searchTerm.toLowerCase().trim();
    if (!search) return this.availableSegments;
    return this.availableSegments.filter(
      s => s.name.toLowerCase().includes(search) || s.value.toLowerCase().includes(search)
    );
  }

  getSegmentName(segValue: string): string {
    return this.availableSegments.find(s => s.value === segValue)?.name ?? segValue;
  }

  updateSearch(gIdx: number, cIdx: number, value: string): void {
    if (this.groups[gIdx]?.conditions[cIdx]) {
      this.groups[gIdx].conditions[cIdx].searchTerm = value;
    }
  }

  // ─── Validation helpers (used by parent) ──────────────────────────────────

  isConditionEmpty(gIdx: number, cIdx: number): boolean {
    return (this.groups[gIdx]?.conditions[cIdx]?.segments.length ?? 0) === 0;
  }

  isGroupAllEmpty(gIdx: number): boolean {
    const group = this.groups[gIdx];
    if (!group) return true;
    return group.conditions.every(c => c.segments.length === 0);
  }

  hasAnySegment(): boolean {
    return this.groups.some(g => g.conditions.some(c => c.segments.length > 0));
  }

  hasEmptyConditions(): boolean {
    return this.groups.some(g => g.conditions.some(c => c.segments.length === 0));
  }

  // ─── Build / Populate ─────────────────────────────────────────────────────

  buildRuleLogic(): RuleLogic {
    const groups: RuleGroup[] = this.groups
      .map(group => ({
        operator: group.conditionOperator,
        conditions: group.conditions
          .filter(c => c.segments.length > 0)
          .map(c => ({
            field: 'segments' as const,
            value: [...c.segments],
            operator: c.segmentOperator,
          })),
      }))
      .filter(g => g.conditions.length > 0);
    return { operator: this.groupOperator, groups };
  }

  populateFromRuleLogic(logic: RuleLogic): void {
    this.groupOperator = logic.operator ?? 'AND';
    this.groups = (logic.groups ?? []).map(group => ({
      conditionOperator: (group.operator as 'AND' | 'OR') ?? 'AND',
      conditions: (group.conditions ?? []).map(c => ({
        segments: [...new Set<string>(c.value ?? [])],
        segmentOperator: (c.operator as 'ANY' | 'ALL') ?? 'ALL',
        dropdownOpen: false,
        searchTerm: '',
      })),
    }));
    if (this.groups.length === 0) {
      this.groups = [this.makeEmptyGroup()];
    }
    this.cdr.detectChanges();
  }

  toggleHelp(): void {
    this.isHelpExpanded = !this.isHelpExpanded;
  }

  toggleJson(): void {
    this.isJsonExpanded = !this.isJsonExpanded;
  }

  getDisplayJson(): string {
    const display = {
      operator: this.groupOperator,
      groups: this.groups.map((group) => ({
        operator: group.conditionOperator,
        conditions: group.conditions.map((cond) => ({
          operator: cond.segmentOperator,
          value: [...cond.segments],
        })),
      })),
    };
    return JSON.stringify(display, null, 2);
  }

  private emit(): void {
    this.ruleLogicChange.emit(this.buildRuleLogic());
  }
}
